import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { globalURLS } from '../../Global/urls';
import { Container, Row, Col, Spinner, Button } from 'react-bootstrap';
import CaptainModal from '../../Components/CaptainModal';
import { IAssesmentPayload, IScreenType } from './helpers/typings';
import BasicDataForm from './forms/BasicDataForm';
import { searchAssessment } from './helpers/utils';
import Assessment from './Assessment';
import { is } from 'date-fns/locale';
import AddScreenForm from './forms/AddScreenForm';

const AssessmentManager: React.FC = () => {
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedAssessment, setSelectedAssessment] =
        useState<IAssesmentPayload>(null);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [openCreateAssessmentModal, setOpenCreateAssessmentModal] =
        useState(false);
    const [openCreateScreenModal, setOpenCreateScreenModal] = useState(false);
    const [selectedScreen, setSelectedScreen] = useState<IScreenType | null>(
        null,
    );
    const [searchQuery, setSearchQuery] = useState('');

    const filteredAssessments = searchAssessment(assessments, searchQuery);

    const getAssessments = async () => {
        try {
            setLoading(true);
            const response = await axios.post(
                `${globalURLS.baseURL}getall_assessments`,
                {},
                globalURLS.config,
            );
            if (response?.data?.success && response?.data?.data) {
                const data = response.data.data;
                // console.log(data);
                setAssessments(data);
            }
        } catch (error) {
            console.log('error', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAssessments();
    }, []);

    return (
        <>
            <Container className="shortener-page">
                <div className="d-flex flex-column align-items-start gap-4 mb-4">
                    <h1 className="mb-0">Assessment Management</h1>
                    {selectedAssessment ? (
                        <div className="d-flex flex-row gap-3 w-100">
                            <Button
                                onClick={() => setSelectedAssessment(null)}
                                style={{ cursor: 'pointer' }}
                                variant="primary"
                            >
                                All Assessments
                            </Button>
                            <Button
                                variant="primary"
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                    setOpenCreateAssessmentModal(true)
                                }
                            >
                                Edit Assessment
                            </Button>
                            <Button
                                variant="primary"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setOpenCreateScreenModal(true);
                                }}
                            >
                                Create Screen
                            </Button>
                            <Button
                                variant="primary"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setIsDuplicating(true);
                                    setOpenCreateAssessmentModal(true);
                                }}
                            >
                                Create Duplicate Assessment
                            </Button>
                        </div>
                    ) : (
                        <div className="d-flex flex-row gap-3 w-100">
                            <Button
                                onClick={() =>
                                    setOpenCreateAssessmentModal(true)
                                }
                                style={{ cursor: 'pointer' }}
                                variant="primary"
                            >
                                Create New Assessment
                            </Button>
                            <input
                                className="px-2 py-1 flex-grow-1"
                                type="search"
                                placeholder="Search Assessments using id, name, or discussionid..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                    )}
                </div>

                {!selectedAssessment ? (
                    <>
                        {loading ? (
                            <div className="d-flex justify-content-center w-100 pt-4">
                                <Spinner animation="border" role="status" />
                            </div>
                        ) : (
                            <Row>
                                {filteredAssessments.length > 0 ? (
                                    filteredAssessments.map(assessment => (
                                        <Col
                                            sm={12}
                                            md={6}
                                            lg={4}
                                            key={assessment.id}
                                            className="mb-4"
                                        >
                                            <div
                                                onClick={() => {
                                                    setSelectedAssessment(
                                                        assessment,
                                                    );
                                                }}
                                                style={{
                                                    height: '80px',
                                                    cursor: 'pointer',
                                                }}
                                                className="assessment-card d-flex justify-content-center align-items-center border shadow p-3 rounded text-center text-decoration-none transition-opacity"
                                            >
                                                {assessment.name ||
                                                    'Untitled Assessment'}
                                            </div>
                                        </Col>
                                    ))
                                ) : (
                                    <p>
                                        {assessments.length === 0
                                            ? 'No assessments available'
                                            : 'No matching assessments found'}
                                    </p>
                                )}
                            </Row>
                        )}
                    </>
                ) : (
                    <Assessment
                        assessment={selectedAssessment}
                        onScreenEdit={() => {
                            setOpenCreateScreenModal(true);
                        }}
                        setAssessmentList={setAssessments}
                        assmentSelector={setSelectedAssessment}
                        screenSelector={setSelectedScreen}
                    />
                )}
            </Container>
            {openCreateAssessmentModal && (
                <BasicDataForm
                    isOpen={openCreateAssessmentModal}
                    onClose={() => {
                        setOpenCreateAssessmentModal(false);
                        isDuplicating && setIsDuplicating(false);
                    }}
                    selectedAssessment={selectedAssessment}
                    isDuplicating={isDuplicating}
                    onSuccess={(payload, mode) => {
                        if (mode === 'create') {
                            setAssessments([...assessments, payload]);
                        } else {
                            setAssessments(prev =>
                                prev.map(a =>
                                    a.id === payload.id ? payload : a,
                                ),
                            );
                        }
                        setSelectedAssessment(payload);
                        isDuplicating && setIsDuplicating(false);
                    }}
                ></BasicDataForm>
            )}

            {openCreateScreenModal && (
                <AddScreenForm
                    isOpen={openCreateScreenModal}
                    onClose={() => {
                        setOpenCreateScreenModal(false);
                        selectedScreen && setSelectedScreen(null);
                    }}
                    onSuccess={(payload, mode) => {
                        if (mode === 'create') {
                            setAssessments([...assessments, payload]);
                        } else {
                            setAssessments(prev =>
                                prev.map(a =>
                                    a.id === payload.id ? payload : a,
                                ),
                            );
                        }
                        setSelectedAssessment(payload);
                    }}
                    selectedAssessment={selectedAssessment}
                    selectedScreen={selectedScreen}
                ></AddScreenForm>
            )}
        </>
    );
};

export default AssessmentManager;
